import * as parser from "@tmcw/togeojson";
import { DOMParser } from "@xmldom/xmldom";

import { Feature } from "geojson";
import { useState } from "react";
import { useDataProvider, useNotify, useRefresh, useResourceContext } from "react-admin";
import { FileRejection } from "react-dropzone";

import { isErrorWithMessage } from "common";

import { ImportDialogState } from "../shared/types";

export const useKmlResourceImportState = <TCreateEntity>(
  handleClose: () => void,
  handleAssertInput: (input: unknown) => asserts input is Feature[],
  handleFilterInput: (input: Feature[]) => Feature[],
  handleMapResourceToCreateEntity: (data: Feature[]) => TCreateEntity[]
) => {
  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const refresh = useRefresh();
  const notify = useNotify();

  const [dialogState, setDialogState] = useState<ImportDialogState<Feature>>({
    step: 0,
    file: null,
    message: null
  });

  const onFileAdded = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    const rejectedFile = rejectedFiles[0];

    if (rejectedFile) {
      setDialogState({
        step: 0,
        file: null,
        message: rejectedFile.errors[0].message
      });

      return;
    }

    const file = acceptedFiles[0];

    if (file) {
      setDialogState({ step: 0, message: null, file });
    }
  };

  const onFileParseError = (e?: unknown) => {
    setDialogState({
      step: -1
    });

    notify(isErrorWithMessage(e) ? e.message : "Invalid KML file type.", {
      type: "error"
    });
  };

  const onFileConfirm = async () => {
    if (dialogState.step === 0 && dialogState.file) {
      try {
        setDialogState({
          file: dialogState.file,
          step: 1,
          importedResources: undefined
        });

        const { features } = parser.kml(
          new DOMParser().parseFromString(await dialogState.file.text(), "text/xml")
        );

        handleAssertInput(features);

        setDialogState({
          file: dialogState.file,
          step: 1,
          importedResources: handleFilterInput(features)
        });
      } catch (e) {
        onFileParseError(e);
      }
    }
  };

  const onSave = async () => {
    if (dialogState.step === 1 && dialogState.importedResources) {
      try {
        setDialogState({ step: 2, status: undefined });

        await dataProvider.createMany(resource, {
          data: handleMapResourceToCreateEntity(dialogState.importedResources)
        });

        setDialogState({ step: 2, status: { status: "success" } });
        refresh();
      } catch (e) {
        if (isErrorWithMessage(e)) {
          setDialogState({ step: 2, status: { status: "error", message: e.message } });
        }
      }
    }
  };

  const onClose = () => {
    handleClose();
    setDialogState({ step: 0, file: null, message: null });
  };

  return {
    dialogState,
    onFileAdded,
    onSave,
    onClose,
    onFileConfirm
  };
};
